import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateCourseModal from '@/components/admin/academy/CreateCourseModal.tsx';
import EditCourseModal from '@/components/admin/academy/EditCourseModal.tsx';
import { faGraduationCap, faSpinner, faArrowLeft, faPlus, faEdit, faTrash, faBook, faEnvelope, faRocket, faEyeSlash, faGift, faUserCheck, faUserXmark } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import PublishModal from '@/components/PublishModal';
import type { Course, CourseID } from '@/models';

const PAGE_SIZE = 50;

export default function AdminCoursesList() {
  const { isStaff } = useUser();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [deletingCourse, setDeletingCourse] = useState<CourseID | null>(null);
  const [publishingCourse, setPublishingCourse] = useState<CourseID | null>(null);
  const [publishModalCourse, setPublishModalCourse] = useState<Course | null>(null);

  useEffect(() => {
    fetchCourses();
  }, [currentPage]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const response = await APIRoutes.listCourses({
        limit: PAGE_SIZE,
        next: currentPage > 1 ? `$${currentPage}` : undefined
      });

      // Validate response and filter out any invalid courses
      const validCourses = (response.courses || []).filter((course: Course) => {
        const isValid = course !== null && course !== undefined && course.id;
        if (!isValid) {
          console.warn('Filtered out invalid course:', course);
        }
        return isValid;
      });

      setCourses(validCourses);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Critical error loading courses list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Kurse');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseCreated = (newCourse: Course) => {
    console.log('handleCourseCreated called with:', newCourse);
    if (newCourse && newCourse.id) {

      setCourses(prev => {
        console.log('Adding new course to list. Previous count:', prev.length);
        const updated = [newCourse, ...prev];
        console.log('New count:', updated.length);
        return updated;
      });
      setTotal(prev => prev + 1);

      showSuccessToast('Kurs erfolgreich erstellt');
    } else {
      console.error('Invalid course passed to handleCourseCreated:', newCourse);
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setIsEditModalOpen(true);
  };

  const handleCourseUpdated = (updatedCourse: Course) => {
    if (updatedCourse && updatedCourse.id) {
      setCourses(prevCourses =>
        prevCourses.map(course =>
          course && course.id === updatedCourse.id ? updatedCourse : course
        ).filter(course => course !== null && course !== undefined)
      );
      showSuccessToast('Kurs erfolgreich aktualisiert');
    }
  };

  const handleDeleteCourse = async (courseId: CourseID) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Kurs löschen möchten? Alle Module und Videos werden ebenfalls gelöscht.')) {
      return;
    }

    try {
      setDeletingCourse(courseId);
      await APIRoutes.deleteCourse(courseId);
      setCourses(prev => prev.filter(course => course.id !== courseId));
      setTotal(prev => prev - 1);
      showSuccessToast('Kurs erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting course:', err);
      showErrorToast('Fehler beim Löschen des Kurses');
      handleAPIError(err);
    } finally {
      setDeletingCourse(null);
    }
  };

  const handlePublishCourse = async () => {
    if (!publishModalCourse) return;

    try {
      setPublishingCourse(publishModalCourse.id);
      const updatedCourse = await APIRoutes.publishCourse(publishModalCourse.id);
      setCourses(prev => prev.map(course => course.id === updatedCourse.id ? updatedCourse : course));
      showSuccessToast('Kurs erfolgreich veröffentlicht');
    } catch (err) {
      console.error('Error publishing course:', err);
      showErrorToast('Fehler beim Veröffentlichen des Kurses');
      handleAPIError(err);
      throw err;
    } finally {
      setPublishingCourse(null);
    }
  };

  const handleUnpublishCourse = async (courseId: CourseID) => {
    if (!confirm('Möchten Sie diesen Kurs wirklich zurückziehen?')) {
      return;
    }

    try {
      setPublishingCourse(courseId);
      const updatedCourse = await APIRoutes.unpublishCourse(courseId);
      setCourses(prev => prev.map(course => course.id === updatedCourse.id ? updatedCourse : course));
      showSuccessToast('Kurs erfolgreich zurückgezogen');
    } catch (err) {
      console.error('Error unpublishing course:', err);
      showErrorToast('Fehler beim Zurückziehen des Kurses');
      handleAPIError(err);
    } finally {
      setPublishingCourse(null);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Kurse nicht verfügbar"
        message={error || 'Die Kurse konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchCourses}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Kurse ({total})
            </h1>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!isStaff}
            title={isStaff ? 'Neuen Kurs erstellen' : 'Nur für Mitarbeiter'}
            className={`bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
            <span>Neuer Kurs</span>
          </button>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && courses.length === 0 && (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faGraduationCap} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Keine Kurse gefunden
            </h3>
            <p className="text-gray-500 dark:text-gray-500 mb-6">
              Erstellen Sie Ihren ersten Kurs.
            </p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              disabled={!isStaff}
              title={isStaff ? 'Kurs erstellen' : 'Nur für Mitarbeiter'}
              className={`bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
            >
              Kurs erstellen
            </button>
          </div>
        )}

        {!loading && courses.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.filter(course => course !== null && course !== undefined).map((course) => (
              <div key={course.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 overflow-hidden relative transition-colors">
                <Link
                  to={`/admin/courses/${course.id}`}
                  className="block"
                >
                  <div className="aspect-video relative overflow-hidden">
                    {course.thumbnail ? (
                      <img
                        src={APIRoutes.getBlobContentUrl(course.thumbnail)}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <FontAwesomeIcon icon={faGraduationCap} className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          {course.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                          {course.description}
                        </p>
                      </div>
                      <div className="flex flex-col items-end ml-2 space-y-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          course.published
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {course.published ? 'Veröffentlicht' : 'Entwurf'}
                        </span>
                        {course.mailSequence && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            course.mailSequence.enabled
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-1" />
                            {course.mailSequence.enabled ? 'Mail aktiv' : 'Mail inaktiv'}
                          </span>
                        )}
                        {course.free && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            <FontAwesomeIcon icon={faGift} className="w-3 h-3 mr-1" />
                            Kostenlos
                          </span>
                        )}
                        {course.onlyCommunityMembers && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            <FontAwesomeIcon icon={faUserCheck} className="w-3 h-3 mr-1" />
                            Nur Community
                          </span>
                        )}
                        {course.excludeCommunityMembers && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                            <FontAwesomeIcon icon={faUserXmark} className="w-3 h-3 mr-1" />
                            Ohne Community
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faBook} className="w-4 h-4 mr-1" />
                          <span>{course.moduleCount ?? 0} Module</span>
                        </div>
                        <div>
                          <span>{course.videoCount ?? 0} Videos</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Erstellt: {formatDate(course.created)}
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Action buttons positioned absolutely to prevent Link click */}
                <div className="absolute bottom-4 right-6 flex items-center space-x-3">
                  <Link
                    to={`/admin/courses/${course.id}/mail-sequence`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location.href = `/admin/courses/${course.id}/mail-sequence`;
                    }}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm"
                    title="Mail-Sequenz verwalten"
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4" />
                  </Link>
                  {/* Publish/Unpublish Button */}
                  {course.published ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnpublishCourse(course.id);
                      }}
                      disabled={!isStaff || publishingCourse === course.id}
                      className={`text-yellow-600 dark:text-yellow-400 disabled:opacity-50 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'cursor-not-allowed' : 'hover:text-yellow-800 dark:hover:text-yellow-300 cursor-pointer'}`}
                      title={isStaff ? 'Zurückziehen' : 'Nur für Mitarbeiter'}
                    >
                      {publishingCourse === course.id ? (
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faEyeSlash} className="w-4 h-4" />
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPublishModalCourse(course);
                      }}
                      disabled={!isStaff || publishingCourse === course.id}
                      className={`text-green-600 dark:text-green-400 disabled:opacity-50 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'cursor-not-allowed' : 'hover:text-green-800 dark:hover:text-green-300 cursor-pointer'}`}
                      title={isStaff ? 'Veröffentlichen' : 'Nur für Mitarbeiter'}
                    >
                      {publishingCourse === course.id ? (
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faRocket} className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditCourse(course);
                    }}
                    disabled={!isStaff}
                    className={`text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer'}`}
                    title={isStaff ? 'Bearbeiten' : 'Nur für Mitarbeiter'}
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteCourse(course.id);
                    }}
                    disabled={!isStaff || deletingCourse === course.id}
                    className={`text-red-600 dark:text-red-400 disabled:opacity-50 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'cursor-not-allowed' : 'hover:text-red-800 dark:hover:text-red-300 cursor-pointer'}`}
                    title={isStaff ? 'Löschen' : 'Nur für Mitarbeiter'}
                  >
                    {deletingCourse === course.id ? (
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
            </div>
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </>
        )}

      {/* Modals */}
      <CreateCourseModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCourseCreated={handleCourseCreated}
      />

      {/* Publish Modal */}
      {publishModalCourse && (
        <PublishModal
          isOpen={!!publishModalCourse}
          onClose={() => setPublishModalCourse(null)}
          onPublish={handlePublishCourse}
          title="Kurs veröffentlichen"
          itemName={publishModalCourse.title}
          type="course"
        />
      )}

      <EditCourseModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        course={editingCourse}
        onCourseUpdated={handleCourseUpdated}
      />
    </>
  );
}
